// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useMemo, useEffect } from "react";
import { Search, X, GripVertical } from "lucide-react";

const EditColumnsModal = ({
  isOpen,
  onClose,
  currentTab,
  tabConfigs,
  currentColumnConfig,
  onApply,
}) => {
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [draggedColumn, setDraggedColumn] = useState(null);

  // Local staged state
  const [localVisibleColumns, setLocalVisibleColumns] = useState(
    currentColumnConfig?.visibleColumns || [],
  );
  const [localFrozenColumns, setLocalFrozenColumns] = useState(
    currentColumnConfig?.frozenColumns || 0,
  );
  const [localColumnWidths, setLocalColumnWidths] = useState(
    currentColumnConfig?.columnWidths || {},
  );

  // Sync local state when modal opens or config changes
  useEffect(() => {
    if (isOpen) {
      const visibleColumns = currentColumnConfig?.visibleColumns || [];
      // Ensure checkbox column is always first
      const orderedColumns = visibleColumns.includes("checkbox")
        ? ["checkbox", ...visibleColumns.filter((col) => col !== "checkbox")]
        : ["checkbox", ...visibleColumns];

      setLocalVisibleColumns(orderedColumns);
      setLocalFrozenColumns(currentColumnConfig?.frozenColumns || 0);
      setLocalColumnWidths(currentColumnConfig?.columnWidths || {});
      setColumnSearchTerm("");
      setDraggedColumn(null);
    }
  }, [isOpen, currentColumnConfig]);

  // Categorize columns
  const getColumnCategory = (column) => {
    const associations = ["bookable_for", "package"];
    const productInfo = [
      "name",
      "sku",
      "price_gross",
      "price_net",
      "unit",
      "status",
      "category",
      "type",
      "location",
      "created_date",
      "last_sync",
      "min_qty",
      "max_qty",
      "layout_position",
      "abbauzeit",
      "aufbauzeit",
      "available_location",
      "anwendbare_gebuehren",
      "besonderheiten",
      "record_id",
      "svg_id",
      "image_url",
    ];

    if (associations.includes(column)) return "ASSOCIATIONS";
    if (productInfo.includes(column)) return "PRODUCT INFORMATION";
    return "OTHER";
  };

  // Get available columns for the modal (use local state)
  const getAvailableColumns = () => {
    const allColumns = tabConfigs[currentTab].columns;

    return allColumns.map((column, index) => ({
      id: column,
      name: tabConfigs[currentTab].headers[index],
      isVisible: localVisibleColumns.includes(column),
      category: getColumnCategory(column),
    }));
  };

  // Filter columns based on search
  const filteredColumns = useMemo(() => {
    const columns = getAvailableColumns();
    if (!columnSearchTerm) return columns;

    return columns.filter((column) =>
      column.name.toLowerCase().includes(columnSearchTerm.toLowerCase()),
    );
  }, [currentTab, columnSearchTerm, localVisibleColumns]); // include localVisibleColumns so checkbox updates reflect immediately

  // Group columns by category
  const groupedColumns = useMemo(() => {
    const groups = {};
    filteredColumns.forEach((column) => {
      if (!groups[column.category]) {
        groups[column.category] = [];
      }
      groups[column.category].push(column);
    });
    return groups;
  }, [filteredColumns]);

  // Local toggle
  const handleLocalToggle = (columnId) => {
    // Prevent toggling the checkbox column
    if (columnId === "checkbox") return;

    setLocalVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((c) => c !== columnId)
        : [...prev, columnId],
    );
  };

  // Local width change
  const handleLocalWidthChange = (columnId, newWidth) => {
    setLocalColumnWidths((prev) => ({
      ...prev,
      [columnId]: parseInt(newWidth) || 150,
    }));
  };

  // Local reorder via drag and drop
  const handleDragStart = (e, columnId) => {
    // Prevent dragging the checkbox column
    if (columnId === "checkbox") {
      e.preventDefault();
      return;
    }
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    // Prevent dropping on or moving the checkbox column
    if (targetColumnId === "checkbox" || draggedColumn === "checkbox") {
      setDraggedColumn(null);
      return;
    }

    if (draggedColumn && draggedColumn !== targetColumnId) {
      const dragIndex = localVisibleColumns.indexOf(draggedColumn);
      const targetIndex = localVisibleColumns.indexOf(targetColumnId);

      if (dragIndex !== -1 && targetIndex !== -1) {
        const newOrder = [...localVisibleColumns];
        const dragged = newOrder[dragIndex];
        newOrder.splice(dragIndex, 1);
        newOrder.splice(targetIndex, 0, dragged);
        setLocalVisibleColumns(newOrder);
      }
    }
    setDraggedColumn(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:border dark:border-slate-700 max-w-6xl w-full mx-2 max-h-[95vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            Choose which columns you see
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-96">
          {/* Left Panel - Available Columns */}
          <div
            className="flex-1 p-4 border-r border-gray-200 dark:border-slate-600 overflow-y-auto"
            style={{ scrollbarWidth: "thin" }}
          >
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search columns..."
                  value={columnSearchTerm}
                  onChange={(e) => setColumnSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:placeholder-slate-400"
                />
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(groupedColumns).map(([category, columns]) => (
                <div key={category}>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    {category}
                  </h4>
                  <div className="space-y-2">
                    {columns.map((column) => (
                      <label
                        key={column.id}
                        className={`flex items-center space-x-2 ${
                          column.id === "checkbox" ? "opacity-60" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={column.isVisible}
                          onChange={() => handleLocalToggle(column.id)}
                          disabled={column.id === "checkbox"}
                          className="rounded border-gray-300 dark:border-slate-500 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:bg-slate-700"
                        />
                        <span
                          className={`text-sm ${
                            column.id === "checkbox"
                              ? "text-gray-500 dark:text-slate-400"
                              : "text-gray-900 dark:text-slate-200"
                          }`}
                        >
                          {column.name}
                          {column.id === "checkbox" && " (Fixed)"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Selected Columns */}
          <div
            className="flex-1 p-4 overflow-y-auto"
            style={{ scrollbarWidth: "thin" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">
                SELECTED COLUMNS ({localVisibleColumns.length})
              </h4>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 dark:text-slate-400">
                  Frozen columns
                </label>
                <select
                  value={localFrozenColumns}
                  onChange={(e) =>
                    setLocalFrozenColumns(parseInt(e.target.value))
                  }
                  className="text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded px-2 py-1"
                >
                  {[0, 1, 2, 3, 4].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              {localVisibleColumns.map((columnId, index) => {
                const column = getAvailableColumns().find(
                  (col) => col.id === columnId,
                );
                if (!column) return null;

                const isCheckbox = columnId === "checkbox";
                const currentWidth = localColumnWidths[columnId] || 150;

                return (
                  <div
                    key={columnId}
                    draggable={!isCheckbox}
                    onDragStart={
                      !isCheckbox
                        ? (e) => handleDragStart(e, columnId)
                        : undefined
                    }
                    onDragOver={!isCheckbox ? handleDragOver : undefined}
                    onDrop={
                      !isCheckbox ? (e) => handleDrop(e, columnId) : undefined
                    }
                    className={`flex items-center justify-between p-2 rounded border ${
                      isCheckbox
                        ? "cursor-default bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 opacity-75"
                        : "cursor-move"
                    } ${
                      index < localFrozenColumns && !isCheckbox
                        ? "bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800"
                        : !isCheckbox
                          ? "bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600"
                          : ""
                    } ${draggedColumn === columnId ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      {isCheckbox ? (
                        <div className="w-4 h-4 flex items-center justify-center">
                          <div className="w-2 h-2 bg-gray-400 dark:bg-slate-500 rounded-full"></div>
                        </div>
                      ) : (
                        <GripVertical className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                      )}
                      <span
                        className={`text-sm ${
                          isCheckbox
                            ? "text-gray-500 dark:text-slate-400"
                            : "text-gray-900 dark:text-slate-200"
                        }`}
                      >
                        {column.name}
                        {isCheckbox && " (Fixed)"}
                      </span>
                    </div>

                    {/* Width input */}
                    <div className="flex items-center space-x-2 mr-2">
                      <label className="text-xs text-gray-500 dark:text-slate-400">
                        Width:
                      </label>
                      <input
                        type="number"
                        min="50"
                        max="500"
                        value={currentWidth}
                        onChange={(e) =>
                          handleLocalWidthChange(columnId, e.target.value)
                        }
                        disabled={isCheckbox}
                        className={`w-16 px-1 py-1 text-xs border rounded ${
                          isCheckbox
                            ? "bg-gray-100 dark:bg-slate-600 text-gray-400 dark:text-slate-500 cursor-not-allowed"
                            : "bg-white dark:bg-slate-600 border-gray-300 dark:border-slate-500 dark:text-slate-200"
                        }`}
                      />
                      <span className="text-xs text-gray-400 dark:text-slate-500">
                        px
                      </span>
                    </div>

                    {!isCheckbox && (
                      <button
                        onClick={() => handleLocalToggle(columnId)}
                        className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {localFrozenColumns > 0 && (
              <div className="mt-4 pt-2 border-t border-gray-200 dark:border-slate-600">
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Above column(s) are frozen
                </p>
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={() => {
                  // Keep only the checkbox column when removing all
                  setLocalVisibleColumns(["checkbox"]);
                }}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                Remove All Columns
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-slate-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // Ensure checkbox column is always first and included
              const finalVisibleColumns = localVisibleColumns.includes(
                "checkbox",
              )
                ? [
                    "checkbox",
                    ...localVisibleColumns.filter((col) => col !== "checkbox"),
                  ]
                : ["checkbox", ...localVisibleColumns];

              onApply?.({
                visibleColumns: finalVisibleColumns,
                frozenColumns: localFrozenColumns,
                columnWidths: localColumnWidths,
              });
              onClose();
            }}
            className="px-4 py-2 bg-orange-500 dark:bg-orange-600 text-white rounded-lg hover:bg-orange-600 dark:hover:bg-orange-500"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditColumnsModal;
